import { Marker, MarkerClusterer } from "@googlemaps/markerclusterer";
import { MapMouseEvent, useMap, AdvancedMarker, Map, Pin, InfoWindow } from "@vis.gl/react-google-maps";
import { useState, useEffect, useRef, useCallback } from "react";
import { Point } from "../model/Point";
import { Route, Segment } from "../model/Route";
import { isDcStation, Place, Station } from "../model/Station";
import { Polyline } from "../Polyline";
import { Button, Typography } from "@mui/material";
import { StationAc } from "./icons/StationAc";
import { StationDc } from "./icons/StationDc";
import { Label } from "@mui/icons-material";

interface ContextMenuState {
    visible: boolean;
    x: number; // Позиция по X для меню
    y: number; // Позиция по Y для меню
    latLng: google.maps.LatLng | null; // Координаты клика
}

export const GoogleMap = (props: {
    route: Route | undefined,
    showOptionalStations: boolean,
    startPoint: Point | undefined,
    endPoint: Point | undefined,
    waypoints: Point[],
    onMapClick: (event: MapMouseEvent) => void,
    onMarkerClick: (place: Place, segment: Segment) => void,
    onAddPointToRoute: (point: Point) => void,
    onStartPointDrag: (point: Point) => void,
    onEndPointDrag: (point: Point) => void,
}) => {

    const [selectedAnchor, setAnchor] = useState<any | null>(null);
    const [selectedSegment, setSelectedSegment] = useState<Segment | undefined>(undefined);

    const map = useMap();
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        visible: false,
        x: 0,
        y: 0,
        latLng: null,
    });
    useEffect(() => {
        if (!map) return;

        // Слушатель для скрытия контекстного меню при любом клике на карту (кроме ПКМ)
        const clickListener = map.addListener('click', () => {
            setContextMenu((prev) => ({ ...prev, visible: false }));
        });

        // Слушатель для открытия контекстного меню при ПКМ
        const rightClickListener = map.addListener('rightclick', (mapsMouseEvent: google.maps.MapMouseEvent) => {
            mapsMouseEvent.stop(); // Остановить стандартное событие контекстного меню браузера

            const domEvent = mapsMouseEvent.domEvent as MouseEvent; // Получаем нативное событие DOM

            setContextMenu({
                visible: true,
                x: domEvent.clientX, // Используем ClientX/Y для позиционирования относительно окна
                y: domEvent.clientY,
                latLng: mapsMouseEvent.latLng,
            });
        });

        return () => {
            google.maps.event.removeListener(clickListener);
            google.maps.event.removeListener(rightClickListener);
        };
    }, [map]);

    return (
        <>
            <Map
                style={{ width: '100vw', height: '100vh' }}
                defaultCenter={{ lat: 51.169392, lng: 71.449074 }}
                defaultZoom={12}
                minZoom={3}
                gestureHandling={'greedy'}
                fullscreenControl={false}
                onClick={(event: MapMouseEvent) => {
                    props.onMapClick(event);
                }}
                disableDefaultUI={false}
                cameraControl={false}
                mapTypeControl={false}
                streetViewControl={false}
                mapId={'3dfe11e1ec4e9a3b'}
                clickableIcons={false}
            >

                <>
                    {props.route?.segments.map((segment, idx) => {
                        return <Polyline
                            key={idx}
                            path={segment.points.map(p => {
                                return { lat: p.latitude, lng: p.longitude }
                            })}
                            strokeColor={segment.color}
                            onMouseOver={(e) => {
                                setAnchor(e.latLng);
                                setSelectedSegment(segment);
                            }}
                            onMouseOut={(e) => {
                                setAnchor(null);
                                setSelectedSegment(undefined);
                            }}
                        />
                    })}
                </>

                {
                    props.startPoint &&
                    <AdvancedMarker
                        position={{ lat: props.startPoint.lat, lng: props.startPoint.lng }}
                        clickable={false}
                        draggable
                        onDragEnd={(e) => {
                            if (e.latLng) {
                                props.onStartPointDrag({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                            }
                        }}
                        zIndex={500}
                    >
                        <Pin background={'#228B22'} glyphColor={'#006400'} borderColor={'#228B22'} />
                    </AdvancedMarker>
                }

                {
                    props.endPoint &&
                    <AdvancedMarker
                        position={{ lat: props.endPoint.lat, lng: props.endPoint.lng }}
                        clickable={false}
                        draggable
                        onDragEnd={(e) => {
                            if (e.latLng) {
                                props.onEndPointDrag({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                            }
                        }}
                        zIndex={500}
                    >
                    </AdvancedMarker>
                }

                {
                    props.waypoints.map((p) => (
                        <AdvancedMarker
                            position={{ lat: p.lat, lng: p.lng }}
                            clickable
                            zIndex={-1}
                        >
                            <Pin background={'#228B22'} borderColor={'#228B22'} />
                        </AdvancedMarker>
                    ))
                }

                <StationMarkers
                    segments={props.route?.segments || []}
                    showOptionalStations={props.showOptionalStations}
                    onMarkerClick={(place, segment) => {
                        props.onMarkerClick(place, segment);
                    }}
                />

                {selectedSegment && selectedAnchor && (
                    <InfoWindow
                        position={selectedAnchor}
                    >
                        <div>
                            {`Сегмент #${selectedSegment.id}`}
                            <br />
                            {`${selectedSegment.length} км.`}
                        </div>
                    </InfoWindow>
                )}
            </Map>
            <ContextMenu
                visible={contextMenu.visible}
                x={contextMenu.x}
                y={contextMenu.y}
                latLng={contextMenu.latLng}
                onGoThroughClick={props.onAddPointToRoute}
                onClose={() => setContextMenu((prev) => ({ ...prev, visible: false }))}
            />
        </>
    );
};

const StationMarkers = (props: { segments: Segment[], showOptionalStations: boolean, onMarkerClick: (place: Place, segment: Segment) => void }) => {
    const map = useMap();
    const [markers, setMarkers] = useState<{ [id: string]: Marker }>({});
    const clusterer = useRef<MarkerClusterer | null>(null);

    const handleClick = useCallback((ev: google.maps.MapMouseEvent, place: Place, segment: Segment) => {
        if (!map) return;
        if (!ev.latLng) return;

        map.panTo(ev.latLng);
        props.onMarkerClick(place, segment);
    }, [map, props]);

    // Initialize MarkerClusterer, if the map has changed
    useEffect(() => {
        if (!map) return;
        if (!clusterer.current) {
            clusterer.current = new MarkerClusterer({ map });
        }
    }, [map]);

    // Update markers, if the markers array has changed
    useEffect(() => {
        clusterer.current?.clearMarkers();
        clusterer.current?.addMarkers(Object.values(markers));
    }, [markers]);

    const setMarkerRef = (marker: Marker | null, key: string) => {
        if (marker && markers[key]) return;
        if (!marker && !markers[key]) return;

        // setMarkers(prev => {
        //   if (marker) {
        //     return { ...prev, [key]: marker };
        //   } else {
        //     const newMarkers = { ...prev };
        //     delete newMarkers[key];
        //     return newMarkers;
        //   }
        // });
    };

    return (
        <>
            {props.segments.flatMap((segment: Segment, idx: number) => {
                const mandatoryPlace = segment.mandatoryPlace
                if (mandatoryPlace) {
                    return (<AdvancedMarker
                        key={mandatoryPlace.id}
                        position={{ lat: mandatoryPlace.location.latitude, lng: mandatoryPlace.location.longitude }}
                        ref={marker => setMarkerRef(marker, mandatoryPlace.id)}
                        clickable={true}
                        onClick={event => handleClick(event, mandatoryPlace, segment)}
                        anchorPoint={['50%', '90%']}
                    >
                        {/* <Typography bgcolor={colors[idx]} color="#fff">#{segment.id}</Typography> */}
                        <b>{segment.mandatoryPlaceSide === 'opposite_side' ? 'L' : 'R'}</b>
                        <br/>
                        <StationDc />
                        {/* {
                                    isDcStation(station) ?
                                        (
                                            <div>
                                                <Typography bgcolor={colors[idx]} color="#fff">#{segment.id}</Typography>
                                                <StationDc />
                                            </div>
                                        ) :
                                        (<StationAc />)
                                } */}
                    </AdvancedMarker>)
                }
                return null
            })
            }
            {props.showOptionalStations && props.segments.flatMap((segment: Segment, idx: number) => (
                segment.optionalPlaces?.map((place: Place) => {
                    return (
                        <AdvancedMarker
                            key={place.id}
                            position={{ lat: place.location.latitude, lng: place.location.longitude }}
                            ref={marker => setMarkerRef(marker, place.id)}
                            clickable={true}
                            onClick={event => handleClick(event, place, segment)}
                            anchorPoint={['50%', '90%']}
                        >
                            {/* <Typography bgcolor={colors[idx]} color="#fff">#{segment.id}</Typography> */}
                            <StationDc />
                            {/* {
                                    isDcStation(station) ?
                                        (
                                            <div>
                                                <Typography bgcolor={colors[idx]} color="#fff">#{segment.id}</Typography>
                                                <StationDc />
                                            </div>
                                        ) :
                                        (<StationAc />)
                                } */}
                        </AdvancedMarker>
                    )
                })
            ))
            }
        </>
    );
};

const ContextMenu: React.FC<ContextMenuState & { onGoThroughClick: (point: Point) => void, onClose: () => void }> = ({ visible, x, y, latLng, onGoThroughClick, onClose }) => {
    if (!visible || !latLng) return null;

    return (
        <div
            style={{
                position: 'fixed', // Или 'absolute', если позиционируете относительно родителя
                left: x,
                top: y,
                backgroundColor: 'white',
                border: '1px solid #ccc',
                boxShadow: '2px 2px 5px rgba(0,0,0,0.2)',
                zIndex: 1000,
                padding: '8px',
                borderRadius: '4px',
                minWidth: '150px',
            }}
            // Предотвращаем закрытие меню при клике внутри него
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{ paddingBottom: '5px', borderBottom: '1px solid #eee', marginBottom: '5px' }}>
                {latLng.lat().toFixed(4)}, {latLng.lng().toFixed(4)}
            </div>
            <Button
                onClick={() => {
                    onGoThroughClick({ lat: latLng.lat(), lng: latLng.lng() });
                    onClose();
                }}
            >
                Проехать через
            </Button>
            {/* Добавьте больше элементов меню */}
        </div>
    );
};